import { useMemo, useState } from "react";
import { Table, Tabs, Tooltip } from "antd";
import type { ColumnsType } from "antd/es/table";
import {
  CheckCircleFilled,
  ExclamationCircleOutlined,
} from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";

import { useScopeParams } from "@/hooks/useScopeParams";
import { apiFetchJson } from "@/utils/apiFetch";
import InfoTooltip from "@/components/UI/InfoTooltip";
import MasteryItemModal from "@/components/DataMastery/MasteryItemModal";
import SiteLineageExplorer from "../components/SiteLineageExplorer";
import type {
  FieldCluster,
  LineageMember,
  MergeEvidence,
} from "../components/SiteLineageExplorer";
import type { MasteredSite } from "../types";

type MasteryVariant = {
  norm_site_id: string;
  name_as_registered: string;
  address: string;
  city: string | null;
  country_code: string | null;
  pi: string | null;
  source_system: string;
  source_label: string;
  trial_id: string;
  trial_name: string | null;
  irb_number: string | null;
  site_number: string | null;
  name_confidence: number | null;
  address_completeness: number | null;
  confidence_pct: number | null;
  confidence_tier: "high" | "medium" | "low";
  key_signals: string[] | null;
  is_canonical: boolean;
  address_flag: boolean;
};

type OpenIssue = {
  id: string;
  entity_key: string;
  issue_type: string;
  entity_display_name: string;
  proposed_canonical?: string;
  status: string;
  priority: string;
  created_at: string | null;
};

type AddressHistoryEntry = {
  line1?: string | null;
  city?: string | null;
  postal?: string | null;
  effective_from?: string | null;
  effective_to?: string | null;
  current?: boolean;
};

type MergedInto = {
  golden_id: string;
  name: string | null;
  evidence_url: string | null;
  decision_basis: string | null;
  run_id: string | null;
};

type GoldenRecord = {
  golden_id: string;
  canonical_name: string | null;
  admission_tier: string | null;
  admission_path: string | null;
  demotion_reason: string | null;
  overall_confidence: number | null;
  data_completeness_pct: number | null;
  merge_count: number | null;
  source_systems: string[];
  address_history: AddressHistoryEntry[];
  site_relocated: boolean;
  merged_into: MergedInto | null;
  absorbed_records: string[];
};

type SiteMasteryEntry = {
  canonical_name: string | null;
  canonical_city: string | null;
  canonical_country_code: string | null;
  golden: GoldenRecord | null;
  variants: MasteryVariant[];
  open_issues: OpenIssue[];
  // raw→golden lineage: the member records behind the golden, what the sources
  // agreed on, and the scored pair that justified the merge
  lineage: LineageMember[];
  field_clusters: FieldCluster[];
  merge_evidence: MergeEvidence | null;
};

const TIER_COLORS: Record<string, { bg: string; text: string; label: string }> =
  {
    GOLDEN: { bg: "#DCFCE7", text: "#15803D", label: "Golden" },
    PROVISIONAL: { bg: "#FEF3C7", text: "#B45309", label: "Provisional" },
    QUARANTINE: { bg: "#FEE2E2", text: "#BE0303", label: "Quarantine" },
  };

const UNTIERED = { bg: "#E8EEF2", text: "#556C75", label: "Untiered" };

// Row union type — issue rows sit above variant rows in the same table
type IssueTableRow = OpenIssue & { _isIssue: true; norm_site_id: string };
type TableRow = MasteryVariant | IssueTableRow;

const ISSUE_TYPE_LABELS: Record<string, string> = {
  field_conflict: "Field conflict",
  geo_conflict: "Geo conflict",
  split_risk: "Split risk",
  cross_fgid_dup: "Cross-ID duplicate",
  duplicate: "Duplicate record", // mastering_pair_review rows
};

const ISSUE_TYPE_COLORS: Record<string, string> = {
  field_conflict: "#F59E0B",
  geo_conflict: "#6366F1",
  split_risk: "#F97316",
  cross_fgid_dup: "#EF4444",
  duplicate: "#EF4444",
};

const PRIORITY_COLORS: Record<string, string> = {
  high: "#EF4444",
  medium: "#F59E0B",
  low: "#9CA3AF",
};

const SOURCE_COLORS: Record<string, { bg: string; text: string }> = {
  takeda: { bg: "#E8F4FB", text: "#0C6E9B" },
  takeda_dqs: { bg: "#EEF4F8", text: "#2B5A6E" },
  takeda_profile: { bg: "#F0F4FF", text: "#3730A3" },
  citeline: { bg: "#FFF7ED", text: "#9A3412" },
};

async function fetchSiteMastery(siteKey: string): Promise<SiteMasteryEntry> {
  return apiFetchJson<SiteMasteryEntry>(
    `${import.meta.env.VITE_API_URL}/data/portfolio/site-mastery/${encodeURIComponent(siteKey)}/`,
  );
}

type MasteryTabProps = {
  site: MasteredSite;
};

export default function MasteryTab({ site }: MasteryTabProps) {
  const { scope, id } = useScopeParams();

  const {
    data: entry,
    isLoading,
    isError,
    refetch,
  } = useQuery<SiteMasteryEntry>({
    queryKey: ["site-mastery", scope, id, site.site_key],
    queryFn: () => fetchSiteMastery(site.site_key),
    enabled: !!scope && !!id,
    staleTime: 5 * 60 * 1000,
  });

  const [activeIssueId, setActiveIssueId] = useState<string | null>(null);

  const variants = useMemo(() => entry?.variants ?? [], [entry?.variants]);

  const sourceFilters = useMemo(
    () =>
      [...new Set(variants.map((v) => v.source_system))].sort().map((s) => ({
        text: variants.find((v) => v.source_system === s)?.source_label ?? s,
        value: s,
      })),
    [variants],
  );

  const nameVariantCount = useMemo(() => {
    return new Set(variants.map((v) => v.name_as_registered)).size;
  }, [variants]);

  const avgConfidence = useMemo(() => {
    const valid = variants.filter(
      (v) => v.confidence_pct !== null && v.confidence_pct > 0,
    );
    if (!valid.length) return null;
    return Math.round(
      valid.reduce((s, v) => s + v.confidence_pct!, 0) / valid.length,
    );
  }, [variants]);

  const golden = entry?.golden ?? null;
  // Prefer the pipeline's golden-record confidence (0–1); fall back to the
  // variant-composite average when unavailable.
  const overallConfidence =
    golden?.overall_confidence != null
      ? Math.round(golden.overall_confidence * 100)
      : avgConfidence;
  const addressHistory = golden?.address_history ?? [];

  const canonicalName = entry?.canonical_name ?? site.site_name;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-48 text-[#8C8C8C] text-[13px]">
        Loading mastery records…
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex flex-col items-center justify-center gap-2 h-48 text-[13px]">
        <span className="text-[#8C8C8C]">
          Couldn&apos;t load mastery records.
        </span>
        <button
          type="button"
          onClick={() => refetch()}
          className="text-[#0C98D0] hover:underline bg-transparent border-0 p-0 cursor-pointer"
        >
          Retry
        </button>
      </div>
    );
  }

  const lineage = entry?.lineage ?? [];

  // A raw member with no normalized_site row yields no variant but still has
  // lineage, so the tab is only truly empty when both are.
  if (!entry || (variants.length === 0 && lineage.length === 0)) {
    return (
      <div className="flex items-center justify-center h-48 text-[#8C8C8C] text-[13px]">
        No mastery records available for this site.
      </div>
    );
  }

  const openIssues = entry.open_issues ?? [];

  // Merge issue rows above variant rows in the same table
  const tableData: TableRow[] = [
    ...openIssues.map(
      (iss): IssueTableRow => ({
        ...iss,
        _isIssue: true,
        norm_site_id: `issue-${iss.id}`,
      }),
    ),
    ...variants,
  ];

  const columns: ColumnsType<TableRow> = [
    {
      title: "Source",
      key: "source",
      width: 130,
      filters: sourceFilters,
      onFilter: (value, r) =>
        "_isIssue" in r ? true : r.source_system === value,
      render: (_, r) => {
        if ("_isIssue" in r) return null;
        const colors = SOURCE_COLORS[r.source_system] ?? {
          bg: "#F3F4F6",
          text: "#374151",
        };
        return (
          <span
            className="inline-block text-[11px] font-medium rounded px-1.5 py-0.5"
            style={{ backgroundColor: colors.bg, color: colors.text }}
          >
            {r.source_label}
          </span>
        );
      },
    },
    {
      title: (
        <span className="flex items-center gap-1">
          Name as registered
          <InfoTooltip
            title="The site name exactly as it appears in the source record. Bold text indicates it is the canonical name this golden record resolves to."
            iconSize={11}
            fill="#9CA3AF"
          />
        </span>
      ),
      key: "name_as_registered",
      width: 260,
      render: (_, r) => {
        if ("_isIssue" in r) {
          const normalized = (r.issue_type ?? "")
            .toLowerCase()
            .replace(/\s+/g, "_");
          const color = ISSUE_TYPE_COLORS[normalized] ?? "#6B7280";
          const label =
            ISSUE_TYPE_LABELS[normalized] ??
            r.issue_type?.replace(/_/g, " ").toLowerCase() ??
            "Issue";
          const priorityColor =
            PRIORITY_COLORS[r.priority?.toLowerCase()] ?? "#9CA3AF";
          return (
            <div className="flex flex-col gap-0.5">
              <span
                className="inline-block text-[11px] font-semibold rounded px-1.5 py-0.5 mb-0.5 self-start"
                style={{ color, background: `${color}18` }}
              >
                {label}
              </span>
              <button
                type="button"
                onClick={() => setActiveIssueId(r.id)}
                className="flex items-center gap-1 text-[12px] font-medium text-[#C2410C] hover:text-[#9A1B0A] hover:underline bg-transparent border-0 p-0 cursor-pointer text-left"
              >
                {r.entity_display_name}
              </button>
              {r.proposed_canonical && (
                <span className="text-[11px] text-[#9CA3AF]">
                  → {r.proposed_canonical}
                </span>
              )}
              <div className="flex items-center gap-1.5 mt-0.5">
                <div
                  className="w-1.5 h-1.5 rounded-full"
                  style={{ background: priorityColor }}
                />
                <span className="text-[10px] text-[#9CA3AF] capitalize">
                  {r.priority} · {r.status}
                </span>
              </div>
            </div>
          );
        }
        return (
          <div>
            <span
              className={`text-[12px] ${r.is_canonical ? "text-[#1F2933] font-semibold" : "text-[#4B5563]"}`}
            >
              {r.name_as_registered}
              {r.is_canonical && (
                <InfoTooltip
                  title="This is the canonical name for this golden record"
                  iconSize={11}
                  fill="#4CAF50"
                />
              )}
            </span>
          </div>
        );
      },
    },
    {
      title: (
        <span className="flex items-center gap-1">
          Confidence
          <InfoTooltip
            title="Composite match confidence (name 60% + address 40%). ≥90% high, ≥70% medium, <70% low."
            iconSize={11}
            fill="#9CA3AF"
          />
        </span>
      ),
      key: "confidence",
      width: 95,
      align: "center" as const,
      render: (_, r) => {
        if ("_isIssue" in r) {
          return (
            <ExclamationCircleOutlined
              style={{ color: "#C2410C", fontSize: 14 }}
            />
          );
        }
        if (!r.confidence_pct)
          return <span className="text-[11px] text-[#9CA3AF]">—</span>;
        const color =
          r.confidence_pct >= 90
            ? "#22C55E"
            : r.confidence_pct >= 70
              ? "#F59E0B"
              : "#EF4444";
        const signals = r.key_signals ?? [];
        const value = (
          <span
            className="text-[12px] font-semibold"
            style={{ color, cursor: signals.length ? "help" : "default" }}
          >
            {r.confidence_pct}%
          </span>
        );
        return signals.length ? (
          <Tooltip
            title={
              <div className="flex flex-col gap-0.5">
                {signals.map((s) => (
                  <span key={s}>{s}</span>
                ))}
              </div>
            }
          >
            {value}
          </Tooltip>
        ) : (
          value
        );
      },
    },
    {
      title: (
        <span className="flex items-center gap-1">
          Address
          <InfoTooltip
            title="Address as recorded in the source. An amber flag means address completeness is below 80%."
            iconSize={11}
            fill="#9CA3AF"
          />
        </span>
      ),
      key: "address",
      width: 220,
      render: (_, r) => {
        if ("_isIssue" in r) return null;
        return (
          <div className="flex items-start gap-1.5">
            <span className="text-[12px] text-[#4B5563]">
              {r.address || "—"}
            </span>
            {r.address_flag && (
              <InfoTooltip
                title="Incomplete address on record"
                iconSize={11}
                fill="#F59E0B"
              />
            )}
          </div>
        );
      },
    },
    {
      title: "Trial",
      key: "trial_id",
      width: 130,
      render: (_, r) => {
        if ("_isIssue" in r) return null;
        return (
          <span className="text-[11px] font-mono text-[#2B4752]">
            {r.trial_id}
          </span>
        );
      },
    },
    {
      title: "PI",
      key: "pi",
      width: 160,
      render: (_, r) => {
        if ("_isIssue" in r) return null;
        return (
          <span className="text-[12px] text-[#4B5563]">{r.pi ?? "—"}</span>
        );
      },
    },
    {
      title: "Site #",
      key: "site_number",
      width: 80,
      align: "center" as const,
      render: (_, r) => {
        if ("_isIssue" in r) return null;
        return (
          <span className="text-[12px] text-[#6B7280]">
            {r.site_number ?? "—"}
          </span>
        );
      },
    },
    {
      title: "IRB #",
      key: "irb_number",
      width: 120,
      render: (_, r) => {
        if ("_isIssue" in r) return null;
        return (
          <span className="text-[12px] text-[#6B7280]">
            {r.irb_number ?? "—"}
          </span>
        );
      },
    },
  ];

  return (
    <>
      <MasteryItemModal
        itemId={activeIssueId}
        onClose={() => setActiveIssueId(null)}
      />
      <div className="flex flex-col gap-4 px-4 pb-4">
        {/* Merged-away banner — this golden was absorbed into another */}
        {golden?.merged_into && (
          <div className="rounded-[8px] border border-[#FAC775] bg-[#FAEEDA] px-4 py-3 flex flex-col gap-1">
            <div className="flex items-center gap-2 text-[12px] font-semibold text-[#854F0B]">
              <ExclamationCircleOutlined style={{ fontSize: 13 }} />
              This record was merged into{" "}
              {golden.merged_into.name ?? golden.merged_into.golden_id}{" "}
              {golden.merged_into.run_id && (
                <span className="font-normal text-[#B45309]">
                  (run {golden.merged_into.run_id})
                </span>
              )}
            </div>
            {golden.merged_into.decision_basis && (
              <span className="text-[12px] text-[#854F0B]">
                {golden.merged_into.decision_basis}
                {golden.merged_into.evidence_url && (
                  <>
                    {" · "}
                    <a
                      href={golden.merged_into.evidence_url}
                      target="_blank"
                      rel="noreferrer"
                      className="text-[#06709B] hover:underline"
                    >
                      evidence
                    </a>
                  </>
                )}
              </span>
            )}
          </div>
        )}

        {/* Golden record panel */}
        <div className="rounded-[8px] border border-[#D1E8D5] bg-[#F6FBF7] p-4 flex flex-col gap-3">
          <div className="flex items-start justify-between gap-3">
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-1.5">
                <CheckCircleFilled style={{ color: "#4CAF50", fontSize: 13 }} />
                <span className="text-[10px] font-semibold text-[#4CAF50] uppercase tracking-wide">
                  Golden Record
                </span>
                {(() => {
                  const tier = golden?.admission_tier ?? null;
                  const c = tier ? (TIER_COLORS[tier] ?? UNTIERED) : UNTIERED;
                  return (
                    <Tooltip
                      title={
                        tier
                          ? `Admission tier ${c.label}${golden?.admission_path ? ` — admitted via ${golden.admission_path}` : ""}${golden?.demotion_reason ? `. Demotion reason: ${golden.demotion_reason}` : ""}`
                          : "This golden record has not yet been assigned an admission tier by the mastering pipeline."
                      }
                    >
                      <span
                        className="inline-block text-[10px] font-semibold rounded px-1.5 py-0.5 uppercase tracking-wide cursor-help"
                        style={{ backgroundColor: c.bg, color: c.text }}
                      >
                        {c.label}
                      </span>
                    </Tooltip>
                  );
                })()}
                {golden?.site_relocated && (
                  <span className="inline-block text-[10px] font-medium rounded px-1.5 py-0.5 bg-[#EEF2FF] text-[#3730A3]">
                    Relocated
                  </span>
                )}
                {(golden?.absorbed_records?.length ?? 0) > 0 && (
                  <Tooltip
                    title={`Absorbed: ${golden!.absorbed_records.join("; ")}`}
                  >
                    <span className="inline-block text-[10px] font-medium rounded px-1.5 py-0.5 bg-[#E1F5EE] text-[#0F6E56] cursor-help">
                      Absorbed {golden!.absorbed_records.length} duplicate
                      {golden!.absorbed_records.length !== 1 ? "s" : ""}
                    </span>
                  </Tooltip>
                )}
              </div>
              <span className="text-[16px] font-semibold text-[#1F2933] leading-snug">
                {canonicalName}
              </span>
            </div>
          </div>

          <div className="grid grid-cols-4 gap-4">
            <div>
              <div className="text-[10px] font-medium text-[#8C8C8C] uppercase tracking-wide mb-1">
                Location
              </div>
              <div className="text-[12px] text-[#1F2933]">
                {[entry.canonical_city, entry.canonical_country_code]
                  .filter(Boolean)
                  .join(", ") ||
                  [site.city, site.country].filter(Boolean).join(", ")}
              </div>
            </div>
            <div>
              <div className="flex items-center gap-1 text-[10px] font-medium text-[#8C8C8C] uppercase tracking-wide mb-1">
                Overall confidence
                <InfoTooltip
                  title="Mastering pipeline confidence that this golden record represents one real site, across all merged source records."
                  iconSize={10}
                  fill="#9CA3AF"
                />
              </div>
              <div
                className="text-[12px] font-semibold"
                style={{
                  color:
                    overallConfidence == null
                      ? "#9CA3AF"
                      : overallConfidence >= 90
                        ? "#22C55E"
                        : overallConfidence >= 70
                          ? "#F59E0B"
                          : "#EF4444",
                }}
              >
                {overallConfidence !== null ? `${overallConfidence}%` : "—"}
              </div>
            </div>
            <div>
              <div className="flex items-center gap-1 text-[10px] font-medium text-[#8C8C8C] uppercase tracking-wide mb-1">
                Records merged
                <InfoTooltip
                  title="Number of raw source records folded into this golden site by the mastering pipeline."
                  iconSize={10}
                  fill="#9CA3AF"
                />
              </div>
              <div className="text-[12px] font-semibold text-[#1F2933]">
                {golden?.merge_count ?? variants.length}
              </div>
            </div>
            <div>
              <div className="flex items-center gap-1 text-[10px] font-medium text-[#8C8C8C] uppercase tracking-wide mb-1">
                Name variants
                <InfoTooltip
                  title="Number of distinct name spellings found across all source records, all resolved to this canonical entity."
                  iconSize={10}
                  fill="#9CA3AF"
                />
              </div>
              <div className="text-[12px] font-semibold text-[#1F2933]">
                {nameVariantCount}
              </div>
            </div>
          </div>

          {/* Address history timeline — real, dated relocations from the pipeline */}
          {addressHistory.length > 1 && (
            <div className="border-t border-[#D1E8D5] pt-3">
              <div className="text-[10px] font-medium text-[#8C8C8C] uppercase tracking-wide mb-2">
                Address history
              </div>
              <div className="flex flex-col gap-1.5">
                {addressHistory.map((a) => (
                  <div
                    key={`${a.effective_from ?? "start"}|${a.line1 ?? ""}|${a.city ?? ""}`}
                    className="flex items-baseline gap-2"
                  >
                    <span
                      className="w-1.5 h-1.5 rounded-full shrink-0 self-center"
                      style={{ background: a.current ? "#4CAF50" : "#C4CDD0" }}
                    />
                    <span
                      className={`text-[12px] ${a.current ? "text-[#1F2933] font-medium" : "text-[#6B7280]"}`}
                    >
                      {[a.line1, a.city, a.postal].filter(Boolean).join(", ")}
                    </span>
                    <span className="text-[11px] text-[#9CA3AF] ml-auto shrink-0">
                      {a.effective_from ?? "—"} →{" "}
                      {a.current ? "current" : (a.effective_to ?? "—")}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Sub-nav: the golden panel above always shows, then one section at a
            time — the page is far too tall with everything stacked. */}
        <Tabs
          defaultActiveKey="records"
          size="small"
          items={[
            {
              key: "records",
              label: `Source records (${tableData.length})`,
              children: (
                <div className="rounded-[8px] border border-[#F0F2F4] bg-white shadow-[0_8px_24px_rgba(15,23,42,0.08)] overflow-hidden">
                  <Table<TableRow>
                    dataSource={tableData}
                    columns={columns}
                    rowKey="norm_site_id"
                    size="small"
                    pagination={
                      tableData.length > 20
                        ? {
                            pageSize: 20,
                            showSizeChanger: false,
                            size: "small",
                          }
                        : false
                    }
                    rowClassName={(r) => {
                      if ("_isIssue" in r)
                        return "!bg-[#FFF8F7] border-l-[3px] border-l-[#FCA5A5]";
                      if (r.is_canonical) return "bg-[#F6FBF7]";
                      return "";
                    }}
                    scroll={{ x: 1100, y: 480 }}
                  />
                </div>
              ),
            },
            {
              key: "agreement",
              label: "Field agreement",
              children: (
                <SiteLineageExplorer
                  lineage={lineage}
                  fieldClusters={entry.field_clusters ?? []}
                  mergeEvidence={entry.merge_evidence ?? null}
                  section="agreement"
                />
              ),
            },
            {
              key: "raw",
              label: "Raw records by source",
              children: (
                <SiteLineageExplorer
                  lineage={lineage}
                  fieldClusters={entry.field_clusters ?? []}
                  mergeEvidence={entry.merge_evidence ?? null}
                  section="records"
                />
              ),
            },
            {
              key: "evidence",
              label: "Merge evidence",
              children: (
                <SiteLineageExplorer
                  lineage={lineage}
                  fieldClusters={entry.field_clusters ?? []}
                  mergeEvidence={entry.merge_evidence ?? null}
                  section="evidence"
                />
              ),
            },
          ]}
        />
      </div>
    </>
  );
}
